import { motion } from 'framer-motion'
import useGameStore from '../stores/gameStore'
import questionsData from '../data/questions.json'

export default function HomeScreen({ onSelectMode }) {
  const { playerLevel, playerXP, totalScore, getWeakQuestionCount } = useGameStore()

  const xpForNextLevel = playerLevel * 1000
  const xpProgress = ((playerXP % 1000) / 1000) * 100
  
  // 苦手問題の数を取得
  const weakCount = getWeakQuestionCount(questionsData)

  const modes = [
    {
      id: 'free',
      name: 'フリー',
      icon: '🎯',
      description: '好きな範囲で順番通り or ランダムに練習',
      color: '#00CED1',
      gradient: 'from-cyan-300 via-blue-300 to-purple-300',
      bgGradient: 'from-cyan-200/30 via-blue-200/30 to-purple-200/30',
    },
    {
      id: 'random',
      name: 'ランダム',
      icon: '⚡',
      description: '全問からランダムに20問出題でハイスコアを狙え',
      color: '#FF1493',
      gradient: 'from-pink-400 via-purple-400 to-magenta-400',
      bgGradient: 'from-pink-200/30 via-purple-200/30 to-magenta-200/30',
    },
    {
      id: 'weak',
      name: '苦手克服',
      icon: '💪',
      description: weakCount > 0 
        ? `正答率の低い${weakCount}問を集中特訓！` 
        : 'まずは他のモードで挑戦しよう',
      color: '#FF6B6B',
      gradient: 'from-red-400 via-orange-400 to-yellow-400',
      bgGradient: 'from-red-200/30 via-orange-200/30 to-yellow-200/30',
      disabled: weakCount === 0,
      badge: weakCount > 0 ? weakCount : null,
    },
    {
      id: 'flashcard',
      name: 'フレーズリスト',
      icon: '📚',
      description: 'ゆっくりインプット＆発音確認',
      color: '#FFD700',
      gradient: 'from-yellow-300 via-orange-300 to-pink-300',
      bgGradient: 'from-yellow-200/30 via-orange-200/30 to-pink-200/30',
    },
  ]

  return (
    <div className="w-full h-full bg-gradient-to-br from-pink-100 via-purple-100 to-blue-100 overflow-auto relative">
      {/* キラキラ背景装飾 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute text-xl"
            initial={{ 
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
              opacity: 0
            }}
            animate={{
              y: [null, Math.random() * window.innerHeight],
              opacity: [0, 0.6, 0],
              rotate: [0, 360]
            }}
            transition={{
              duration: 4 + Math.random() * 3,
              repeat: Infinity,
              delay: Math.random() * 3,
              ease: "easeInOut"
            }}
          >
            {['✨', '⭐', '💫'][Math.floor(Math.random() * 3)]}
          </motion.div>
        ))}
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="font-display text-4xl md:text-5xl font-black mb-6 bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 bg-clip-text text-transparent drop-shadow-lg">
            ✨ 限界チャレンジ1 English ✨
          </h1>
        </motion.div>

        {/* Player Info */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="card-neon mb-8 max-w-md mx-auto"
        >
          <div className="flex items-center gap-4 mb-4">
            <motion.div
              className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-400 to-purple-400 flex items-center justify-center text-3xl shadow-lg"
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              ⚡
            </motion.div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <span className="font-display text-purple-600 text-lg font-black">Lv.{playerLevel}</span>
                <span className="text-pink-500 text-sm font-bold">「Blooming」🌸</span>
              </div>
              <div className="w-full bg-gradient-to-r from-pink-200 to-purple-200 h-4 rounded-full overflow-hidden shadow-inner">
                <motion.div
                  className="h-full bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 rounded-full shadow-lg"
                  initial={{ width: 0 }}
                  animate={{ width: `${xpProgress}%` }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                />
              </div>
            </div>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-purple-600 font-semibold">トータルスコア</span>
            <span className="text-pink-600 font-display font-black text-lg">💎 {totalScore.toLocaleString()}</span>
          </div>
        </motion.div>

        {/* Mode Selection Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {modes.map((mode, index) => (
            <motion.button
              key={mode.id}
              initial={{ opacity: 0, y: 20, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.3 + index * 0.1, type: "spring" }}
              onClick={() => !mode.disabled && onSelectMode(mode.id)}
              className={`card-neon transition-all duration-300 min-h-[140px] relative overflow-hidden group ${
                mode.disabled ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              whileHover={!mode.disabled ? { scale: 1.05, y: -5, rotate: [0, -2, 2, -2, 0] } : {}}
              whileTap={!mode.disabled ? { scale: 0.95 } : {}}
            >
              {/* グラデーション背景 */}
              <div className={`absolute inset-0 bg-gradient-to-br ${mode.bgGradient} opacity-50 group-hover:opacity-70 transition-opacity`} />
              
              {/* Content */}
              <div className="relative z-10">
                <div className="flex items-center gap-4 mb-3">
                  <motion.span
                    className="text-5xl"
                    animate={!mode.disabled ? { rotate: [0, 15, -15, 0] } : {}}
                    transition={{ duration: 2, repeat: Infinity, delay: index * 0.3 }}
                  >
                    {mode.icon}
                  </motion.span>
                  <h2 className={`font-display text-2xl font-black bg-gradient-to-r ${mode.gradient} bg-clip-text text-transparent`}>
                    {mode.name}
                  </h2>
                  {/* バッジ表示 */}
                  {mode.badge && (
                    <motion.span
                      className="bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs font-black px-2 py-1 rounded-full shadow-lg"
                      animate={{ scale: [1, 1.1, 1] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      {mode.badge}問
                    </motion.span>
                  )}
                </div>
                <p className="text-purple-700 text-sm text-left font-semibold">
                  {mode.description}
                </p>
              </div>
              
              {/* 装飾キラキラ */}
              <motion.div
                className="absolute top-2 right-2 text-xl"
                animate={{ rotate: [0, 360], scale: [1, 1.2, 1] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                ✨
              </motion.div>
            </motion.button>
          ))}
        </div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="text-center mt-12 text-purple-600 text-base font-bold"
        >
          <p>💖 100のフレーズで、あなたらしい英語を育てよう 💖</p>
        </motion.div>
      </div>
    </div>
  )
}
